import { getUserDetail, UserDetail } from '@lovoldsystem/core.client'
import { DeleteOutline, ExpandMore, Save } from '@mui/icons-material'
import {
  Box,
  Typography,
  useTheme,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
} from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateSchemaForm, useUpdateFormSchemaReport } from 'src/api/form-schema'
import { useNavigateToDeviation } from 'src/hooks/useNavigateToDeviation'
import { FormFieldType } from 'src/enums'
import { IDeviation } from 'src/generated-types/deviation'
import { IDeviationType } from 'src/generated-types/deviation-type'
import { FormField, FormRow, getFieldKey } from 'src/generated-types/form-field'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getFormattedDate, getThemeColor, GetRGBA } from 'src/utils'
import { hasPermission } from '../common/Authorization'
import FieldSelector from '../formGenerator/FieldSelector'
import { ChecklistDeviationMenu, DeviationMenuState } from './ChecklistDeviationMenu'
import { ChecklistFieldsLayout } from './ChecklistFieldsLayout'
import { useChecklistValuesForm } from './checklist-utils'
import { ChecklistField } from '../formGenerator/ChecklistField'
import { GridActionButton } from '../common/GridActionButton'
import { useDialogCtx } from 'src/hooks/context-hooks'
import CreateDeviationDialog from 'src/screens/localities/deviations/CreateDeviation'
import { IFormSchemaReport } from 'src/generated-types/form-schema'

export interface IChecklistAccordionItem {
  report: IFormSchemaReport
  expanded: boolean
  onToggle: (reportId: number | undefined) => void
  onDelete?: (id: number) => void
  disabled?: boolean
  hideDelete?: boolean
  createPermissions: number[]
  deletePermissions: number[]
  permission: number
  customerId: EntityId
  localityId?: EntityId
  serviceStationId?: EntityId
  storageId?: EntityId
  scheduledTaskId?: number
}

const ChecklistAccordionItem: React.FC<IChecklistAccordionItem> = ({
  report,
  expanded,
  onToggle,
  onDelete,
  disabled,
  hideDelete,
  deletePermissions,
  permission,
  customerId,
  localityId,
  serviceStationId,
  storageId,
  scheduledTaskId,
}) => {
  const { t } = useTranslation(['checklist', 'common', 'deviation'])
  const theme = useTheme()
  const navigateToDeviation = useNavigateToDeviation()
  const { showDialog } = useDialogCtx()
  const [editedValues, setEditedValues] = useState<Record<string, any>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [deviationMenuAnchor, setDeviationMenuAnchor] = useState<DeviationMenuState | null>(null)

  const reportId = report.id
  const { mutateAsync: updateReport } = useUpdateFormSchemaReport(customerId as EntityId, reportId as EntityId)

  const checklistName = report?.formSchemaVersion?.formSchema?.name ?? t('checklist', { ns: 'checklist' })
  const createdBy = report?.createdByUser ? getUserDetail(report.createdByUser, UserDetail.FullName) : '-'
  const createdAt = report?.createdAt ? getFormattedDate(new Date(report.createdAt + 'Z'), 'YYYY-MM-DD, HH:mm') : '-'

  const getItemDeviations = (fieldId: string, itemId: string): IDeviation[] => {
    if (!reportId) return []

    // Deviations linked directly on the report (MainComponent / activity log reports)
    const directDeviations =
      report.formSchemaReportDeviations
        ?.filter(frd => frd.checklistFieldId === fieldId && frd.checklistItemId === itemId)
        .map(frd => frd.deviation as unknown as IDeviation)
        .filter((deviation): deviation is IDeviation => Boolean(deviation)) ?? []

    // Deviations linked through scheduled tasks
    const scheduledTaskDeviations =
      report.scheduledTask?.scheduledTaskDeviations
        ?.filter(
          std =>
            std.deviation &&
            std.deviation.formSchemaReportDeviations?.some(
              frd =>
                frd.formSchemaReportId === reportId &&
                frd.checklistFieldId === fieldId &&
                frd.checklistItemId === itemId,
            ),
        )
        .map(std => std.deviation!) ?? []

    const merged = [...directDeviations, ...scheduledTaskDeviations]
    return merged.filter((deviation, index) => merged.findIndex(other => other.id === deviation.id) === index)
  }

  const handleOpenDeviationMenu = (event: React.MouseEvent<HTMLElement>, fieldId: string, itemId: string) => {
    event.stopPropagation()
    setDeviationMenuAnchor({ element: event.currentTarget, itemId, fieldId })
  }

  const handleOpenAddDeviationModal = (event: React.MouseEvent<HTMLElement>, fieldId: string, itemId: string) => {
    event.stopPropagation()
    showDialog(
      CreateDeviationDialog,
      {
        componentProps: {
          customerId: customerId,
          localityId: localityId as EntityId,
          scheduledTaskId: scheduledTaskId,
          deviationType: IDeviationType.Task,
          deviationTypeFromParent: IDeviationType.Task,
          checklistFieldMetadata: {
            formSchemaReportId: reportId,
            fieldId: fieldId,
            itemId: itemId,
          },
        },
        opts: { maxWidth: 'lg' },
        isAutoCloseDisabled: true,
      },
      undefined,
      true,
    )
  }

  const handleCloseDeviationMenu = () => {
    setDeviationMenuAnchor(null)
  }

  const handleViewDeviation = (deviationId: EntityId) => {
    handleCloseDeviationMenu()
    navigateToDeviation(deviationId, { customerId, localityId, serviceStationId, storageId })
  }

  const parsedValues = useMemo(() => {
    try {
      const original = report.values ? JSON.parse(report.values) : {}
      return { ...original, ...editedValues }
    } catch {
      return { ...editedValues }
    }
  }, [report.values, editedValues])

  const handleFieldChange = useCallback((fieldKey: string, value: any) => {
    setEditedValues(prev => ({ ...prev, [fieldKey]: value }))
    setHasUnsavedChanges(true)
  }, [])

  const saveChanges = async () => {
    if (disabled || !reportId || !hasUnsavedChanges) return

    const updatedValues = { ...parsedValues }
    setIsSaving(true)
    await updateReport(
      {
        values: JSON.stringify(updatedValues),
        formSchemaVersionId: report.formSchemaVersionId!,
        type: report.type,
        scheduledTaskId: report.scheduledTaskId,
        localityId: report.localityId,
        isDraft: report.isDraft ?? false,
      } as any,
      {
        onSuccess: () => {
          setHasUnsavedChanges(false)
          setEditedValues({})
          invalidateSchemaForm.getFormSchemaReports(
            customerId,
            report.type,
            undefined,
            undefined,
            undefined,
            scheduledTaskId,
            localityId ? Number(localityId) : undefined,
          )
          toast.success(t('checklist-updated-successfully', { ns: 'checklist' }))
        },
        onError: () => {
          toast.error(t('error-updating-checklist', { ns: 'checklist' }))
        },
      },
    )
    setIsSaving(false)
  }

  const layout: FormRow[] = useMemo(() => {
    const versionLayout = report.formSchemaVersion?.layout
    if (!versionLayout) return []
    if (typeof versionLayout === 'string') {
      try {
        return JSON.parse(versionLayout)
      } catch {
        return []
      }
    }
    return versionLayout
  }, [report.formSchemaVersion])

  const pseudoForm = useChecklistValuesForm(parsedValues, handleFieldChange)

  const renderField = (field: FormField, layoutIndex: number, fieldIndex: number) => {
    const fieldKey = getFieldKey(field)

    if (field.type === FormFieldType.checklist) {
      return (
        <ChecklistField
          field={field}
          disabled={disabled || isSaving}
          form={pseudoForm as any}
          onChange={value => {
            handleFieldChange(fieldKey, value)
          }}
          onDragStart={() => {}}
          setSelectedSettingField={() => {}}
          getItemDeviations={getItemDeviations}
          handleOpenDeviationMenu={handleOpenDeviationMenu}
          handleOpenAddDeviationModal={handleOpenAddDeviationModal}
          customerId={customerId}
          scheduledTaskId={scheduledTaskId}
          reportId={reportId}
        />
      )
    }
    return (
      <FieldSelector
        field={field}
        layoutIndex={layoutIndex}
        index={fieldIndex}
        setSelectedSettingField={() => {}}
        disabled={false}
        fieldDisabled={disabled || isSaving}
        onChange={value => {
          handleFieldChange(fieldKey, value)
        }}
        form={pseudoForm as any}
      />
    )
  }

  const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    onToggle(isExpanded ? report.id : undefined)
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (report.id && onDelete) {
      onDelete(report.id)
    }
  }

  return (
    <>
      <StyledAccordion expanded={expanded} onChange={handleAccordionChange}>
        <StyledAccordionSummary expandIcon={<ExpandMore />}>
          <Flex.Row alignItems="center" gap={2} flex={1}>
            <Box flex={1}>
              <Typography fontWeight={600} fontSize="14px">
                {checklistName}
                {hasUnsavedChanges && (
                  <Typography component="span" fontSize="12px" color="warning.main" sx={{ ml: 1 }}>
                    ({t('unsaved-changes', { ns: 'checklist' })})
                  </Typography>
                )}
              </Typography>
              <Typography fontSize="12px" color="text.secondary">
                {createdBy} • {createdAt}
              </Typography>
            </Box>
            <Flex.Row gap={1} onClick={e => e.stopPropagation()}>
              {hasUnsavedChanges && (
                <GridActionButton
                  size="small"
                  width="30px"
                  onClick={saveChanges}
                  disabled={disabled || isSaving}
                  toolTipTitle={t('save', { ns: 'common' }) as string}
                  buttonIcon={<Save fontSize="small" />}
                />
              )}
              {!hideDelete && (
                <GridActionButton
                  size="small"
                  width="30px"
                  onClick={handleDeleteClick}
                  disabled={disabled || !hasPermission(deletePermissions, permission)}
                  toolTipTitle={t('delete', { ns: 'common' }) as string}
                  buttonIcon={<DeleteOutline fontSize="small" />}
                />
              )}
            </Flex.Row>
          </Flex.Row>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <Box>
            <ChecklistFieldsLayout
              layout={layout}
              renderField={renderField}
              emptyContent={
                <Typography sx={{ color: getThemeColor(theme, MUIThemeColors.secondaryMain), fontStyle: 'italic' }}>
                  {t('no-form-fields', { ns: 'checklist' })}
                </Typography>
              }
            />
          </Box>
        </StyledAccordionDetails>
      </StyledAccordion>

      <ChecklistDeviationMenu
        anchor={deviationMenuAnchor}
        onClose={handleCloseDeviationMenu}
        deviations={
          deviationMenuAnchor ? getItemDeviations(deviationMenuAnchor.fieldId, deviationMenuAnchor.itemId) : []
        }
        onViewDeviation={handleViewDeviation}
      />
    </>
  )
}

export default ChecklistAccordionItem

export const StyledAccordion = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${GetRGBA(getThemeColor(theme, MUIThemeColors.black), 0.2)}`,
  borderRadius: '4px !important',
  marginBottom: '4px',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 4px 0',
  },
}))

export const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  minHeight: '44px !important',
  maxHeight: '44px !important',
  height: '44px !important',
  padding: '0 16px',
  '& .MuiAccordionSummary-content': {
    margin: '10px 0 !important',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: '10px 0 !important',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginRight: '8px',
  },
  '&:hover': {
    backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
  },
  '&.Mui-expanded': {
    minHeight: '44px !important',
    maxHeight: '44px !important',
  },
}))

export const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '16px',
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  borderTop: `1px solid ${GetRGBA(getThemeColor(theme, MUIThemeColors.black), 0.2)}`,
}))
